/**
 * useWizardState, v10
 * State odpovídá přesně struktuře ValuationRequest backendu.
 */
import { pocetKroku } from '../utils/kroky';
import { useState, useCallback } from 'react';

const INITIAL_STATE = {
  currentStep: 1,

  // Úvodní krok, zadání vlastními slovy a popisy nahraných závěrek.
  // Soubory samotné drží utils/podklady.js, do stavu se nedají uložit.
  zadani: { text: '', soubory: [], cesta: null },

  // Segment, vybrán v Step0, benchmarky dostupné ve všech krocích
  segment_id: '',
  segmentBenchmarks: null, // { benchmark_ebitda_margin, benchmark_rrs, ... } z /api/segments

  // Krok 1, Údaje o společnosti
  company: {
    company_name: '',
    ico: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_position: '',
  },

  // Krok 2, Finance (hodnoty vždy v plných Kč)
  financeUnit: 'thousands', // jednotka použitá při ručním zadávání: 'czk'|'thousands'|'millions'
  finance: {
    revenue: '',
    revenue_prev_year: '',
    revenue_2y_ago: '',
    cogs: '',
    opex: '',
    inventory: '',
    // Q3M YoY srovnání (volitelné)
    revenue_last_3m: '',
    revenue_last_3m_prev_year: '',
    // Kamenná pobočka
    has_physical_stores: false,
    physical_store_count: '',
    physical_store_revenue_pct: '',
    physical_store_costs: '',
    // Normalizace EBITDA (volitelná sekce)
    normalize_ebitda: false,
    owner_salary_actual: '',
    owner_salary_market: '',
    personal_expenses_through_business: '',
    one_time_costs: '',
    owner_operates: true,
    // Rozvaha (volitelná sekce)
    showBalanceSheet: false,
    net_income: '',
    total_assets: '',
    equity: '',
    current_liabilities: '',
    total_liabilities: '',
    interest_bearing_debt: '',
    cash: '',
  },

  // Krok 2, KPIs (slim v12: AOV, order_count, CAC, LTV odstraněny)
  kpi: {
    rrs: '',
    rrs_unknown: false,
    monthly_ad_spend: '',
    monthly_ad_spend_unknown: false,
    roas: '',
    roas_unknown: false,
    ltv: '',
    ltv_unknown: false,
    cac: '',
    cac_unknown: false,
    repeat_purchase_rate: '',
    repeat_purchase_rate_unknown: false,
  },

  // Krok 3, Traffic & Brand (bez předvýběru, uživatel musí vědomě zvolit)
  traffic_brand: {
    traffic_mix: '',
    brand_mix: '',
    channel_dependency: '',
  },

  // Krok 4, Kvalitativní faktory
  qualitative: {
    segment_id: '',
    own_production_pct: 0,
    own_brand_pct: 0,
    private_label_pct: 0,
    resale_pct: 100,
    fulfillment: '',
    founder_dependency: '',
    total_sku_count: '',
    product_concentration_pct: 30,
    customer_concentration_pct: '',
    total_supplier_count: '',
    single_supplier_revenue_pct: 0,
    market_entry_barriers: '',
    product_consumability: '',
    business_age_years: '',
    revenue_trend: 'stable_growth',
  },

  // Krok 2b, Očištění zisku (volitelný)
  profitCleaning: {
    skipped: true,          // výchozí: přeskočen
    hours_per_week: 40,
    owner_pay: '',
    market_replacement: '',
    market_replacement_touched: false,
    personal_costs: '',
    one_time_costs: '',
    family_wages: '',
    marketing_test: '',
    sde: null,
    adjusted_ebitda: null,
    raw_ebitda: null,
    wage_correction: null,
    total_addbacks: null,
  },

  // Vybraný plán a výsledky
  selectedPlan: 'free',  // 'free' | 'pro', zvoleno v Step5
  valuationResult: null,
  auditResult: null,
  aiReport: null,       // { tier, ...reportData }, generovaný AI výkladový report
  errorMessage: null,
};

export function useWizardState() {
  const [wizardState, setWizardState] = useState(INITIAL_STATE);

  const nextStep = useCallback(() => {
    setWizardState(prev => ({
      ...prev,
      currentStep: Math.min(prev.currentStep + 1, pocetKroku(prev.typ_podnikani)),
    }));
  }, []);

  const previousStep = useCallback(() => {
    setWizardState(prev => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - 1, 1),
    }));
  }, []);

  const reset = useCallback(() => {
    setWizardState(INITIAL_STATE);
  }, []);

  return { wizardState, setWizardState, nextStep, previousStep, reset };
}
